import { OptionProps } from "components/common/utlis/TypeAnnotations";
import secureLocalStorage from "react-secure-storage";
import { useEffect, useState } from "react";
import { Option } from "components/common/services/ValidationService";
import { timePeriods } from "./ManageReports";
import { selectAuth } from "features/auth/AuthSlice";
import { useSelector } from "react-redux";
import {
  ACCOUNT_MANAGER,
  AREA_MANAGER,
  CENTRAL_DATA_MANAGEMENT_MICROSERVICE,
  OFFICE_MANAGER,
  RECRUITER,
  SUPER_ADMIN,
  ADMIN
} from "Constants";
import { ApiCall } from "components/common/services/ApiServices";
import * as ENDPOINTS from "routes/ApiEndpoints";
import SelectWithSearch from "components/common/atoms/SelectWithSearch";
import { t } from "../translation/Translation";
import Calender from "components/common/molecules/Calender";
import { useNavigate } from "react-router-dom";
import { downloadAuthedFile } from "components/common/services/apiUtils";
import Button from "components/common/atoms/Button";
import Reset from "static/images/Reset";
import EncryptDecryptService from "components/common/services/EncryptDecryptService";
import NoRecords from "components/common/atoms/NoRecords";
import RightArrow from "static/images/RightArrow";
import { RootState } from "store";
import { createQueryParams, filterAndMerge, getQueryParamsFromRedux } from "components/common/utlis/HelperFunctions";
import { clearDestinationPath, setDestinationPath } from "features/NavigationSlice";
import { useDispatch } from "react-redux";
import PaginationNew from "components/common/atoms/PaginationNew";
import MultiSelectAtom from "components/common/atoms/MultiSelectAtom";
import SpinnerWrapper from "components/common/atoms/SpinnerWrapper";
import AccessControl from "components/common/services/RolesAndPermissions/AccessControl";
import CustomNotify from "components/common/atoms/CustomNotify";
import PdfIcon from "static/images/Pdf";

interface SearchData {
  type: string;
  initialFetch?: boolean;
  office: OptionProps[];
  managers?: number[];
  recruiters?: Option | null;
  timePeriod?: Option | null;
  from?: Date | null;
  to?: Date | null;
  currentPage: number;
  recordsPerPage: number;
  orderByColumn: string;
  orderIn: "" | "asc" | "desc";
}

interface SearchOptions {
  offices: OptionProps[];
  managers: OptionProps[];
  recruiters: OptionProps[];
}

interface ReportRecord {
  office: string;
  officeId: number;
  userId: number | string;
  name: string;
  number: string;
  startDate: string;
  endDate: string;
  year: string;
  documentExists?: boolean;
}

const ManageAccountManagerReports: React.FC = () => {
  const authUser = useSelector(selectAuth);
  // Report PDF language must follow the application language switcher (Header.tsx writes
  // "site_lang"), not navigator.language - the browser locale is unrelated to the app language.
  const appLang = secureLocalStorage.getItem("site_lang") ?? "nl";
  const [searchOptions, setSearchOptions] = useState<SearchOptions>({
    offices: [],
    managers: [],
    recruiters: [],
  });
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const getInitialType = () => {
    let type = "";
    if (authUser.role === RECRUITER) {
      type = "recruiter";
    } else if (authUser.role === SUPER_ADMIN || authUser.role === ADMIN ||authUser.role === ACCOUNT_MANAGER || authUser.role === AREA_MANAGER || authUser.role === OFFICE_MANAGER) {
      type = "am";
    }
    // else if (authUser.role === ADMIN || authUser.role === OFFICE_MANAGER) {
    //   type = "teamLead"
    // }
    return type;
  }
  const destinationPath = useSelector(
    (state: RootState) => state.navigation.destinationPath
  );
  const queryParams = getQueryParamsFromRedux(destinationPath);

  // Initialize search data based on query params
  const defaultSearchData: SearchData = {
    type: "accountManager",
    initialFetch: true,
    office: [],
    timePeriod: {
      label: "Weekly",
      value: 1
    },
    managers: [],
    from: null,
    to: null,
    currentPage: 1,
    recordsPerPage: 10,
    orderByColumn: "",
    orderIn: "",
  };

  const [reports, setReports] = useState<ReportRecord[]>([]);
  const [timePeriod, setTimePeriod] = useState<Option | null>(null);
  const navigate = useNavigate();

  const [paginationData, setPaginationData] = useState({
    totalPages: 1,
    totalRecords: 1,
    cursor: null,
  });

  //advanced search
  const defaultAdvSearchData = {
    street: "",
  };

  const defaultTabData = {
    tabFieldName: "",
    tabFieldValue: getInitialType(),
  };

  const filteredSearchData = filterAndMerge(defaultSearchData, queryParams);
  const filteredAdvSearchData = filterAndMerge(
    defaultAdvSearchData,
    queryParams
  );
  const filteredTabData = filterAndMerge(defaultTabData, queryParams);

  // Initialize search data based on query params, merging with default values
  const [searchData, setSearchData] = useState<SearchData>({
    ...defaultSearchData,
    ...filteredSearchData,
  });

  const [advSearchData, setAdvSearchData] = useState({
    ...defaultAdvSearchData,
    ...filteredAdvSearchData,
  });

  const [activeTab, setActiveTab] = useState({
    ...defaultTabData,
    ...filteredTabData,
  });

  useEffect(() => {
    const filterData = { ...searchData, ...advSearchData, ...activeTab };
    const searchUrl = createQueryParams(filterData);
    dispatch(setDestinationPath(searchUrl));
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchData(filterData);
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchData]);

  const fetchData = async (searchFilters = {}, page = 1, initial = false) => {
    const data = {
      ...searchFilters,
    };

    const response = await ApiCall.service(
      ENDPOINTS.FETCH_REPORTS,
      "POST",
      data,
      true,
      CENTRAL_DATA_MANAGEMENT_MICROSERVICE
    );

    if (response?.status === 200) {
      setReports(response?.data?.reports?.records);
      setPaginationData(response?.data?.reports?.paginationData);
      setTimePeriod(searchData?.timePeriod ?? null);

      setSearchOptions((prevData) => ({
        ...prevData,
        recruiters: response?.data?.managers ?? [],
        managers: response?.data?.managers ?? [],
        offices: response?.data?.offices ?? [],
      }));
      setLoading(false);
    } else {
      CustomNotify({
        type: "error",
        message: t(response?.message)
      })
    }
  };

  const handleDateChange = (date: Date | null, name: string) => {
    setSearchData((prevSearch) => ({
      ...prevSearch,
      [name]: date,
      currentPage: 1,
    }));
  };

  const timePeriodLinks: { [key: number]: string } = {
    1: "/am/weekly/report",
    2: "/am/monthly/report",
  };
  const link =
    typeof timePeriod?.value === "number"
      ? timePeriodLinks[timePeriod.value] || null
      : null;

  const handlePageChange = (pageNumber: number) => {
    setSearchData((prevSearch) => ({
      ...prevSearch,
      currentPage: pageNumber,
    }));
  };

  const handleSelectChange = (selectedOption: any, fieldName: string) => {
    setSearchData((prevSearch) => ({
      ...prevSearch,
      currentPage: 1,
      fromDashboard: false,
      [fieldName]: selectedOption,
    }));
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setSearchData((prev) => ({
      ...prev,
      recordsPerPage: newItemsPerPage,
      currentPage: 1,
    }));
  };

  const handleReset = async (e: React.FormEvent) => {
    dispatch(clearDestinationPath());
    setSearchData({ ...defaultSearchData, office: [] });
    setAdvSearchData(defaultAdvSearchData);
    setActiveTab(defaultTabData);
  };

  const handleButton = (item: any) => {
    navigate(`${link}?data=${encodeURIComponent(EncryptDecryptService.encryptData(JSON.stringify(item)))}`)
  }

  return (
    <AccessControl
      requiredPermissions={[
        {
          permission: "Reports",
          read: true,
        },
      ]}
      renderNoAccess={true}
    >
      <div className="row">
        <div className="col-md-2">
          <SelectWithSearch
            title={t("Office")}
            name="office"
            value={searchData.office}
            search={true}
            onChange={(e) => {
              handleSelectChange(e, "office");
            }}
            options={searchOptions.offices}
            className="col-md-2"
            isTranslate={true}
          />
        </div>

        {(authUser.role === AREA_MANAGER || authUser.role === ADMIN || authUser.role === SUPER_ADMIN) &&
          <div className="col-md-2">
            <MultiSelectAtom
              title={t("Account manager")}
              onChange={(selectedOption) =>
                handleSelectChange(selectedOption, "managers")
              }
              name="managers"
              options={searchOptions.managers ?? []}
              value={searchData.managers ?? []}
            />
          </div>
        }
        <div className="col-md-2">
          <SelectWithSearch
            title={t("Time period")}
            name="timePeriod"
            value={searchData.timePeriod}
            search={true}
            onChange={(e) => {
              handleSelectChange(e, "timePeriod");
            }}
            options={timePeriods}
            className="col-md-2"
            isSelectOption={false}
          />
        </div>
        <div className="col-md-2 position-relative">
          <Calender
            label={t("From")}
            selectedDate={searchData.from}
            isMandatory={false}
            name="from"
            onChange={handleDateChange}
            showMonthDropdown={false}
            showYearDropdown={false}
            minDate={new Date("01/01/2024")}
            maxDate={new Date()}
          />
        </div>
        <div className="col-md-2 position-relative">
          <Calender
            label={t("To")}
            selectedDate={searchData.to}
            onChange={handleDateChange}
            isMandatory={false}
            name="to"
            showMonthDropdown={false}
            showYearDropdown={false}
            minDate={new Date("01/01/2024")}
            maxDate={new Date()}
          />
        </div>
        <div className="col-2 text-end mt-34">
          <Button
            title=""
            type="submit"
            handleClick={handleReset}
            className="btn delete-btn rounded-3 shadow-none  me-3 px-3 reset-btn"
          >
            <span title={t("Reset")}>
              <Reset />
            </span>
          </Button>
        </div>
      </div>
      <div className="row">
        <div className="table-responsive">
          <table className="table table-hover">
            <thead>
              <tr className="TableHeader">
                <th className="ps-lg-4">{t("Office")}</th>
                {authUser.role !== ACCOUNT_MANAGER && (
                  <th>{t("Account manager")}</th>
                )}
                {timePeriod?.value === 2 ? (
                  <>
                    <th>{t("Month number")}</th>
                    <th>{t("Monthly date")}</th>
                  </>
                ) : (
                  <>
                    <th>{t("Week number")}</th>
                    <th>{t("Weekly date")}</th>
                  </>
                )}
                <th>{t("Year")}</th>
                {/* <AccessControl
                                    requiredPermissions={[{
                                        permission: "Follow-up",
                                        update: true,
                                    }]}
                                    actions={true}
                                > */}
                <th>{t("Action")}</th>
                {/* </AccessControl> */}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <SpinnerWrapper headerLength={authUser.role !== ACCOUNT_MANAGER ? 6 : 5} />
              ) : (
                reports && reports.length > 0 ? (
                  reports.map((item: ReportRecord, index) => {
                    const user = {
                      amId: item.userId,
                      periodNumber: item.number,
                      officeId: item.officeId,
                      year: item.year,
                    };

                    return (
                      <tr key={index}>
                        <td className="ps-lg-4 text-break">{item?.office}</td>
                        {authUser.role !== ACCOUNT_MANAGER && (
                          <td>{item?.name}</td>
                        )}
                        <td>{item?.number}</td>
                        <td>{`${item?.startDate} to ${item?.endDate}`}</td>
                        <td>{item?.year}</td>
                        {link ? (
                          <td className="table-action-icons">
                            {/* CR 608: monthly reports (timePeriod 2) are always downloadable
                                (generated on demand when no approved PDF exists); weekly keeps
                                the documentExists gate. Previous condition: {(item.documentExists) && ( */}
                            {(timePeriod?.value === 2 || item.documentExists) && (
                              <a
                                className="marginRightPoint5"
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  // TO-38233: the CDM download endpoint now requires a Bearer
                                  // token, so fetch it authed and save from a blob (a plain
                                  // <Link> navigation can't send the Authorization header).
                                  downloadAuthedFile(
                                    `/api/report/download-pdf?data=${encodeURIComponent(JSON.stringify({
                                      ...user,
                                      userId: item.userId,
                                      type: "accountManager",
                                      reportType: timePeriod?.value === 1 ? "weekly" : "monthly",
                                      userRole: authUser.role || "ADMIN",
                                      appLangKey: appLang,
                                      userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
                                    }))}&name=report_${item.year}&lang=${appLang}&year=${item.year}`,
                                    `report_${item.year}.pdf`,
                                    process.env.REACT_APP_CDM_SERVICE
                                  );
                                }}
                                title={t("Download PDF")}
                              >
                                <PdfIcon />
                              </a>
                            )}
                            <span
                              className="cursor-pointer"
                              onClick={() => handleButton(user)}>
                              <RightArrow />
                            </span>
                          </td>
                        ) : null}
                      </tr>
                    );
                  })
                ) : (
                  <NoRecords headerLength={authUser.role !== ACCOUNT_MANAGER ? 6 : 5} message={t("No reports found")} />
                )
              )
              }
            </tbody>
          </table>
          {!loading && (
            <div className="pagination d-block">
              <PaginationNew
                currentPage={searchData.currentPage}
                totalPages={paginationData.totalPages}
                totalRecords={paginationData.totalRecords}
                recordsPerPage={searchData.recordsPerPage}
                onItemsPerPageChange={handleItemsPerPageChange}
                onPageChange={(newPage: any) => handlePageChange(newPage)}
              />
            </div>
          )}
        </div>
      </div>
    </AccessControl>
  );
};
export default ManageAccountManagerReports;
